import { useEffect, useState } from 'react';
import { useStore } from '@nanostores/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  HStack,
  Heading,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  VStack,
} from '@astryxdesign/core';

import { formatDate, formatNumber, formatPrice, type AdminUserRow } from '../lib/api';
import { $adminUsers, $adminUsersError, $adminUsersLoading, loadAdminUsers } from '../stores/admin';
import { $auth } from '../stores/auth';

const statusVariant = (status: string): 'success' | 'warning' | 'error' => {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'error';
  return 'warning';
};

export function AdminPage() {
  const auth = useStore($auth);
  const data = useStore($adminUsers);
  const loading = useStore($adminUsersLoading);
  const error = useStore($adminUsersError);
  const [page, setPage] = useState(1);
  const isAdmin = auth.user?.is_admin ?? false;

  useEffect(() => {
    if (isAdmin) void loadAdminUsers(page, 50);
  }, [isAdmin, page]);

  if (!isAdmin) {
    return (
      <EmptyState
        isCompact
        title="Not authorized"
        description="This page is restricted to admin accounts."
      />
    );
  }

  if (loading && !data) {
    return <Spinner size="lg" label="Loading users" />;
  }

  return (
    <VStack gap={3}>
      <VStack gap={0.5}>
        <Heading level={1}>Admin</Heading>
        <Text color="secondary">Account and payment overview across all users.</Text>
      </VStack>

      {error && <Banner status="error" title="Could not load users" description={error} />}

      {data && (
        <>
          <div className="stat-grid">
            <Stat label="Total users" value={formatNumber(data.total_users)} />
            <Stat label="Paying users" value={formatNumber(data.paying_users)} />
            <Stat label="Page" value={`${data.page} / ${Math.max(data.total_pages, 1)}`} />
          </div>

          <Card padding={3} variant="default">
            <VStack gap={2}>
              <HStack gap={1} justify="between" vAlign="center" wrap="wrap">
                <Text weight="semibold">Users</Text>
                <HStack gap={1}>
                  <Button
                    label="Previous"
                    variant="secondary"
                    size="sm"
                    isDisabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  />
                  <Button
                    label="Next"
                    variant="secondary"
                    size="sm"
                    isDisabled={page >= data.total_pages}
                    onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                  />
                </HStack>
              </HStack>

              {data.users.length === 0 ? (
                <EmptyState isCompact title="No users yet" description="Registered accounts will appear here." />
              ) : (
                <Table density="compact" dividers="rows" hasHover>
                  <TableHeader>
                    <TableRow>
                      <TableHeaderCell>Email</TableHeaderCell>
                      <TableHeaderCell>Name / company</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>Plan</TableHeaderCell>
                      <TableHeaderCell>Payment</TableHeaderCell>
                      <TableHeaderCell>Joined</TableHeaderCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.users.map((u) => (
                      <UserRow key={u.id} user={u} />
                    ))}
                  </TableBody>
                </Table>
              )}
            </VStack>
          </Card>
        </>
      )}
    </VStack>
  );
}

function UserRow({ user }: { user: AdminUserRow }) {
  return (
    <TableRow>
      <TableCell>
        <span className="mono">{user.email}</span>
      </TableCell>
      <TableCell>
        <VStack gap={0}>
          <Text size="sm">{user.name || '—'}</Text>
          <Text size="xsm" color="secondary">
            {user.company || '—'}
          </Text>
        </VStack>
      </TableCell>
      <TableCell>
        <Badge label={user.status} variant={statusVariant(user.status)} />
      </TableCell>
      <TableCell>
        <span className="mono">{user.plan_code}</span>
      </TableCell>
      <TableCell>
        {user.subscription ? (
          <VStack gap={0}>
            <Text size="sm">{formatPrice(user.subscription.price_cents, user.subscription.currency)}</Text>
            <Text size="xsm" color="secondary">
              {user.subscription.provider} · {user.subscription.current_period}
            </Text>
          </VStack>
        ) : (
          <Text size="sm" color="secondary">
            No active payment
          </Text>
        )}
      </TableCell>
      <TableCell>
        <Text size="sm" color="secondary">
          {formatDate(user.created_at)}
        </Text>
      </TableCell>
    </TableRow>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card padding={2} variant="muted">
      <VStack gap={0.5}>
        <Text size="xsm" color="secondary">
          {label}
        </Text>
        <Text size="lg" weight="semibold">
          {value}
        </Text>
      </VStack>
    </Card>
  );
}
